import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import omit from 'lodash/omit';
import Link from 'next/link';
import { FocusEventHandler, MouseEventHandler, ReactElement, RefObject, useCallback, useRef } from 'react';
import { useTheme } from 'styled-components';

import { FontWeights } from '../../utils/typography';
import Loader from '../Loader';
import type { CalmTheme } from '../ThemeProvider';
import { ButtonIcon, Wrapper } from './styles';
import {
	ButtonProps,
	FocusRingVariants,
	FocusRingVariantsType,
	OptionalBackgroundButtonProps,
} from './types';

export type { FocusRingVariantsType };

export { FocusRingVariants, ButtonSizes } from './types';

export const OptionalBackgroundButton = ({
	backgroundColor,
	borderColor,
	children,
	className,
	contentAlignment,
	Icon,
	iconColor,
	isDisabled,
	isLoading,
	maxWidth,
	legacyOnClick,
	onPress,
	onPressStart,
	textColor,
	fontWeight,
	height,
	type: _type,
	fullWidth,
	loaderColor,
	openInNewTab,
	tabIndex = 0,
	focusRingVariant = FocusRingVariants.Light,
	size: _size,
	hideShadow,
	buttonRef: _buttonRef,
	UNSAFE_showFocusRing,
	...props
}: OptionalBackgroundButtonProps): ReactElement => {
	// TODO: clean up / standardize ref handling
	const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
	const buttonRef = _buttonRef ?? ref;
	const theme = useTheme() as typeof CalmTheme;
	const size = _size ?? theme.buttons.defaultSize;

	// Handle both useFocusRing and any custom focus callbacks
	const { isFocusVisible, focusProps } = useFocusRing({ within: true });
	const { onFocus: focusPropsOnFocus, onBlur: focusPropsOnBlur } = focusProps;
	const { onFocus: propsOnFocus, onBlur: propsOnBlur } = props;
	const onFocus: FocusEventHandler<HTMLElement> = e => {
		focusPropsOnFocus?.(e);
		propsOnFocus?.(e);
	};
	const onBlur: FocusEventHandler<HTMLElement> = e => {
		focusPropsOnBlur?.(e);
		propsOnBlur?.(e);
	};

	const isInternalLink = props.href?.startsWith('/');
	const elementType = props.href ? (isInternalLink ? Link : 'a') : 'button';
	const type = elementType === 'a' || elementType === Link ? undefined : _type ?? 'button';

	const { buttonProps } = useButton(
		{
			isDisabled: isDisabled || isLoading,
			type,
			onPress,
			onPressStart,
			elementType: elementType === Link ? 'a' : elementType,
			...props,
		},
		buttonRef,
	);

	const allowedProps = (() => {
		// Removes role='button' from normal links.
		// That role is only meant for when it does not behave as a link
		return elementType === 'a' || elementType === Link ? omit(buttonProps, ['role']) : buttonProps;
	})();

	const { onMouseEnter: buttonPropsOnMouseEnter, onMouseLeave: buttonPropsOnMouseLeave } = allowedProps;
	const { onMouseEnter: propsOnMouseEnter, onMouseLeave: propsOnMouseLeave } = props;
	const onMouseEnter: MouseEventHandler<HTMLButtonElement> = useCallback(
		e => {
			buttonPropsOnMouseEnter?.(e);
			propsOnMouseEnter?.(e);
		},
		[buttonPropsOnMouseEnter, propsOnMouseEnter],
	);
	const onMouseLeave: MouseEventHandler<HTMLButtonElement> = useCallback(
		e => {
			buttonPropsOnMouseLeave?.(e);
			propsOnMouseLeave?.(e);
		},
		[buttonPropsOnMouseLeave, propsOnMouseLeave],
	);

	const iconOnly = Boolean(Icon && !isLoading && !children);

	return (
		<Wrapper
			as={elementType}
			data-testid={`${elementType === Link ? 'Link' : elementType}-element-type`}
			$backgroundColor={backgroundColor}
			$borderColor={borderColor}
			$focusRingVariant={focusRingVariant}
			className={className}
			$isDisabled={isDisabled}
			{...props}
			{...allowedProps}
			{...focusProps}
			target={elementType !== 'button' && openInNewTab ? '_blank' : undefined}
			type={type}
			$isFocusVisible={Boolean(isFocusVisible || UNSAFE_showFocusRing)}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onFocus={onFocus}
			onBlur={onBlur}
			$contentAlignment={contentAlignment}
			$textColor={textColor || 'white'}
			$fontWeight={fontWeight ?? FontWeights.Medium}
			$height={height}
			$fullWidth={fullWidth}
			$maxWidth={maxWidth}
			// legacyOnClick is deprecated, and would be overwritten by allowedProps if not passed in *after* it here
			{...(legacyOnClick ? { onClick: legacyOnClick } : {})}
			$isLoading={isLoading}
			$size={size}
			tabIndex={tabIndex}
			$hasIconAndText={Boolean(Icon) && !iconOnly}
			$hideShadow={hideShadow}
			ref={buttonRef as RefObject<HTMLButtonElement>}
		>
			{!isLoading && Icon && (
				<ButtonIcon
					$color={iconColor || textColor || 'white'}
					$isDisabled={isDisabled}
					$iconOnly={iconOnly}
					$size={size}
				>
					<Icon />
				</ButtonIcon>
			)}
			{children}
			{isLoading ? <Loader color={loaderColor} /> : null}
		</Wrapper>
	);
};

/**
 *
 * Displays a Button based on:
 * https://www.figma.com/file/RSwYY8r0WWLfGH04ky7sxX/Calm---Web?node-id=1600%3A0
 *
 * * optionally displays a svg icon on the button (`icon={<Twitter />}`)
 * * optionally left-aligns the button label
 * * optionally renders as an `<a>` tag if href is passed in
 * * optionally calls onPress if passed in as a prop
 *
 */
const Button = (props: ButtonProps): ReactElement => <OptionalBackgroundButton {...props} />;

export const PrimaryButton = (props: OptionalBackgroundButtonProps): ReactElement => {
	const theme = useTheme() as typeof CalmTheme;
	return (
		<Button
			backgroundColor="buttonPrimaryBg"
			textColor="buttonPrimaryText"
			loaderColor="buttonPrimaryLoader"
			hideShadow={theme.buttons?.primaryHideShadow}
			focusRingVariant={theme.buttons?.primaryFocusRingVariant}
			{...props}
		/>
	);
};

export const SecondaryButton = (props: OptionalBackgroundButtonProps): ReactElement => {
	const theme = useTheme() as typeof CalmTheme;
	return (
		<Button
			backgroundColor="buttonSecondaryBg"
			textColor="buttonSecondaryText"
			loaderColor="buttonSecondaryLoader"
			hideShadow={theme.buttons?.secondaryHideShadow}
			focusRingVariant={theme.buttons?.secondaryFocusRingVariant}
			{...props}
		/>
	);
};

export default Button;
